import {RouteComponentProps} from 'react-router-dom';
import Box from '@amzn/awsui-components-react/polaris/box';
import Table from '@amzn/awsui-components-react/polaris/table';
import React, {useEffect, useState} from 'react';
import {CheckDynamicExecutionResponse, DynamicExecutionsHistory} from '../../../backend/model';
import {fetchDynamicExecutionDetails, fetchDynamicExecutions} from '../../../backend/backend';
import {emptyTableDiv, getFilterCounterText, PROPERTY_FILTERING_I18N_CONSTANTS} from '../../TableTemplate';
import {defaultSortColumn, dynamicExecutionHistoryColumns, FILTERING_OPTIONS, FILTERING_PROPERTIES} from './DynamicExecutionHistoryTableMetadata';
import { PropertyFilter, Pagination } from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import AjaxStatusIndicator from '../../basic/AjaxStatusIndicator';
import { parseErrorMessage } from '../../../utils/errors';
import DynamicExecutionDetails from './DynamicExecutionDetailsPanel';

type WorkflowHistoryPageProps = RouteComponentProps<{ executionId?: string }>

const DynamicExecutionHistoryPage: React.FC<WorkflowHistoryPageProps> = (props) => {
    const [dynamicExecutionHistory, setDynamicExecutionHistory] = useState<DynamicExecutionsHistory>()
    const [dynamicExecutionResponse, setDynamicExecutionResponse] = useState<CheckDynamicExecutionResponse>()
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessages, setErrorMessages] = useState<string[]>([])

    const executionIdFromUrl = (props.match.params.executionId as string)?.trim() || ''

    const resetState = (): void => {
        setDynamicExecutionHistory(undefined)
        setDynamicExecutionResponse(undefined)
        setErrorMessages([])
    }

    useEffect(() => {
        getDynamicExecutionHistory(executionIdFromUrl)
    }, [props, executionIdFromUrl])

    const getDynamicExecutionHistory = async (executionId: string,) => {
        resetState()

        setIsLoading(true)
        if (executionId) {
            await fetchDynamicExecutionByExecutionIdTask(executionId)
        } else if (!executionId || executionId == '') {
            setDynamicExecutionHistory((await fetchDynamicExecutions()).data)
        }
        setIsLoading(false)
    }

    async function fetchDynamicExecutionByExecutionIdTask(executionId: string) {
        try {
            setDynamicExecutionResponse((await fetchDynamicExecutionDetails(executionId)).data)
        } catch (error: any) {
            console.log(error)
            setErrorMessages([parseErrorMessage(error)])
            setDynamicExecutionResponse(undefined)
        }
    }

    const {items, filteredItemsCount, collectionProps, propertyFilterProps, paginationProps} = useCollection(
        dynamicExecutionHistory?.executions ?? [],
        {
            propertyFiltering: {
                filteringProperties: FILTERING_PROPERTIES,
                empty: emptyTableDiv,
                noMatch: emptyTableDiv,
            },
            pagination: {
                pageSize: 20,
            },
            sorting: {
                defaultState: {
                    sortingColumn: dynamicExecutionHistoryColumns[defaultSortColumn],
                },
            },
        },
    )

    const tableContent = (
        <Table
        id='dynamicExecutionHistoryTable'
        columnDefinitions={dynamicExecutionHistoryColumns}
        items={items}
        wrapLines
        empty={emptyTableDiv}
        {...collectionProps}
        pagination={<Pagination
            {...paginationProps}
        />}
        filter={
            <PropertyFilter
                {...propertyFilterProps}
                filteringOptions={FILTERING_OPTIONS}
                i18nStrings={PROPERTY_FILTERING_I18N_CONSTANTS}
                countText={getFilterCounterText(filteredItemsCount || 0)}
            />
        }/>
    )

    return <div id='dynamic-execution'>
        <Box variant="h1">Dynamic Executions</Box>
        <br/>
        <AjaxStatusIndicator errorMessage={errorMessages.join(' ')} loading={isLoading}/>
        {dynamicExecutionResponse && <DynamicExecutionDetails response={dynamicExecutionResponse}/>}
        {dynamicExecutionHistory && tableContent}
    </div>
}

export default DynamicExecutionHistoryPage
